import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {Button} from '@/components/ui/button'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {ErrorBanner} from '@/components/ui/error-banner'
import {toast} from '@/hooks/use-toast'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {formatTimestamp, formatRelativeTime, formatIsoDuration} from '@/lib/date-utils'
import {MutedText} from '@/components/shared/MutedText'
import {PageLayout} from '@/components/shared/PageLayout'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {useIsEmbedded} from '@/components/shared/EmbeddedContext'
import {InlineRow} from '@/components/shared/InlineRow'
import {CONCEPT_ICON} from '@/lib/concepts'
import {IconText} from '@/components/shared/IconText'
import {RefreshCw, Trash2} from 'lucide-react'

const CACHES_QUERY_KEY = ['admin', 'caches'] as const

export function CachesAdmin() {
    const i18n = useI18n()
    const services = useServices()
    const queryClient = useQueryClient()
    const {hasPermission} = useAuth()
    const embedded = useIsEmbedded()
    usePageTitle(embedded ? undefined : i18n.caches.pageTitle)

    const canReload = hasPermission(permissions.caches.reload)

    const {data: caches, isLoading, error} = useQuery({
        queryKey: CACHES_QUERY_KEY,
        queryFn: () => services.cachesAdmin.list(),
    })

    const evictMutation = useMutation({
        mutationFn: (name: string) => services.cachesAdmin.evict(name),
        onSuccess: () => {
            toast({title: i18n.caches.evictSuccess})
            queryClient.invalidateQueries({queryKey: CACHES_QUERY_KEY})
        },
        onError: () => toast({variant: 'destructive', title: i18n.caches.evictError}),
    })

    const reloadMutation = useMutation({
        mutationFn: (name: string) => services.cachesAdmin.reload(name),
        onSuccess: () => {
            toast({title: i18n.caches.reloadSuccess})
            queryClient.invalidateQueries({queryKey: CACHES_QUERY_KEY})
        },
        onError: () => toast({variant: 'destructive', title: i18n.caches.reloadError}),
    })

    if (error) {
        return <ErrorBanner error={error}/>
    }

    const rows = caches ?? []

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.cache}
            title={i18n.caches.title}
            description={i18n.caches.subtitle}
            count={rows.length}
            data-testid="caches-list"
            data-help-prefix="caches"
        >
            <Table data-testid="caches-table" data-help-prefix="list">
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name" data-help-key="name">{i18n.caches.columns.name}</TableHead>
                        <TableHead data-testid="col-description" data-help-key="description">{i18n.caches.columns.description}</TableHead>
                        <TableHead data-testid="col-entries" data-help-key="entries">{i18n.caches.columns.entries}</TableHead>
                        <TableHead data-testid="col-last-load" data-help-key="lastLoad">{i18n.caches.columns.lastLoad}</TableHead>
                        <TableHead data-testid="col-ttl" data-help-key="ttl">{i18n.caches.columns.ttl}</TableHead>
                        <TableHead data-testid="col-next-expiry" data-help-key="nextExpiry">{i18n.caches.columns.nextExpiry}</TableHead>
                        <TableHead data-testid="col-reload-trigger" data-help-key="reloadTrigger">{i18n.caches.columns.reloadTrigger}</TableHead>
                        {canReload && (
                            <TableHead data-testid="col-actions" data-help-key="actions">{i18n.caches.columns.actions}</TableHead>
                        )}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!isLoading && rows.length === 0 && (
                        <TableEmptyState
                            colSpan={canReload ? 8 : 7}
                            hasFilters={false}
                            matchMessage={i18n.caches.emptyState}
                            emptyMessage={i18n.caches.emptyState}
                        />
                    )}
                    {rows.map((cache) => (
                        <TableRow key={cache.name} data-testid={`cache-row-${cache.name}`} data-readonly>
                            <TableCell>{cache.name}</TableCell>
                            <TableCell><MutedText>{cache.description}</MutedText></TableCell>
                            <TableCell>{cache.estimatedSize}</TableCell>
                            <TableCell>
                                {cache.lastLoadInstant
                                    ? formatTimestamp(cache.lastLoadInstant)
                                    : <MutedText>{i18n.caches.neverLoaded}</MutedText>}
                            </TableCell>
                            <TableCell data-testid={`cache-ttl-${cache.name}`}>{formatIsoDuration(cache.ttl)}</TableCell>
                            <TableCell>
                                {cache.nextExpiryInstant
                                    ? formatRelativeTime(cache.nextExpiryInstant)
                                    : <MutedText>{i18n.caches.expired}</MutedText>}
                            </TableCell>
                            <TableCell><MutedText size="sm">{cache.reloadTrigger}</MutedText></TableCell>
                            {canReload && (
                                <TableCell>
                                    <InlineRow gap="sm">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            data-testid={`evict-${cache.name}`}
                                            disabled={evictMutation.isPending && evictMutation.variables === cache.name}
                                            onClick={() => evictMutation.mutate(cache.name)}
                                        >
                                            <IconText icon={Trash2}>{i18n.caches.actions.evict}</IconText>
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            data-testid={`reload-${cache.name}`}
                                            disabled={reloadMutation.isPending && reloadMutation.variables === cache.name}
                                            onClick={() => reloadMutation.mutate(cache.name)}
                                        >
                                            <IconText icon={RefreshCw}>{i18n.caches.actions.reload}</IconText>
                                        </Button>
                                    </InlineRow>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
